<template>
  <div :class="$style.wrapper">
    <div v-if="isLoading" :class="$style.loading">Загрузка...</div>

    <div v-else-if="!model" :class="$style.error">Модель не найдена</div>

    <div v-else>
      <!-- Размеры в наличии -->
      <div v-if="availableSizes.length > 0" :class="$style.section">
        <h2 :class="$style.sectionTitle">В НАЛИЧИИ</h2>
        <div :class="$style.sizesList">
          <label
            v-for="sizeItem in availableSizes"
            :key="`available-${sizeItem.id}`"
            :class="[
              $style.sizeItem,
              {
                [$style.selected]:
                  selectedSizeId !== null &&
                  Number(selectedSizeId) === Number(sizeItem.id),
              },
            ]"
          >
            <input
              type="radio"
              name="size-selection"
              :value="String(sizeItem.id)"
              :checked="Number(selectedSizeId) === Number(sizeItem.id)"
              @change="selectSize($event.target.value)"
              :class="$style.radioInput"
            />
            <span :class="$style.sizeLabel"
              >{{ sizeItem.size }} {{ sizeItem.sizeType }}</span
            >
          </label>
        </div>
      </div>

      <!-- Размеры не в наличии -->
      <div v-if="unavailableSizes.length > 0" :class="$style.section">
        <h2 :class="$style.sectionTitle">НЕТ В НАЛИЧИИ</h2>
        <div :class="$style.sizesList">
          <label
            v-for="sizeItem in unavailableSizes"
            :key="`unavailable-${sizeItem.id}`"
            :class="[$style.sizeItem, $style.disabled]"
          >
            <input
              type="radio"
              :value="sizeItem.id"
              disabled
              :class="$style.radioInput"
            />
            <span :class="$style.sizeLabel"
              >{{ sizeItem.size }} {{ sizeItem.sizeType }}</span
            >
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SelectSize",
  data() {
    return {
      model: null,
      sizes: [],
      selectedSizeId: null,
      isLoading: true,
      mainButtonInstance: null,
      mbHandler: null,
      totalAvailableCount: null, // Общее количество доступных продуктов модели от продавца
    }
  },
  computed: {
    modelId() {
      return this.$route.params.modelId
    },
    sellerId() {
      return this.$route.query.sellerId
    },
    availableSizes() {
      return this.sizes.filter((size) => size.isAvailable === true)
    },
    unavailableSizes() {
      return this.sizes.filter((size) => size.isAvailable === false)
    },
    selectedSizeItem() {
      if (this.selectedSizeId === null) return null
      return this.sizes.find(
        (s) => Number(s.id) === Number(this.selectedSizeId)
      )
    },
  },
  async mounted() {
    await this.fetchModelSizes()
    this.setupMainButton()
  },
  beforeUnmount() {
    this.destroyMainButton()
  },
  watch: {
    selectedSizeId() {
      this.updateMainButton()
    },
  },
  methods: {
    async fetchModelSizes() {
      this.isLoading = true
      try {
        // Получаем информацию о модели и продуктах из заявки
        const bidItems = this.$store.state.bid || []
        const existingItem = bidItems.find(
          (item) => String(item.modelId) === String(this.modelId)
        )

        // Получаем модель через API для получения продуктов продавца
        const modelResponse = await this.$store.dispatch("getModel", {
          id: this.modelId,
        })
        const modelData = modelResponse.data || modelResponse
        const apiModel = modelData.model || {}
        const sellerItems = modelData.sellers?.items || []

        // Фильтруем продукты по sellerId, если он указан
        const filteredItems = this.sellerId
          ? sellerItems.filter(
              (item) => String(item.seller?.id) === String(this.sellerId)
            )
          : sellerItems

        // Сохраняем модель
        this.model = {
          id: apiModel.id,
          title: apiModel.title,
          images: apiModel.images || [],
        }

        // Вычисляем общее количество доступных продуктов от продавца
        // Суммируем count из всех продуктов продавца
        const totalAvailableCount = filteredItems.reduce((sum, item) => {
          return sum + (item.count ? Number(item.count) : 0)
        }, 0)
        this.totalAvailableCount =
          totalAvailableCount > 0 ? totalAvailableCount : null

        // Создаем мапу продуктов продавца по размеру (size + size_type)
        // Используем массив, чтобы сохранить все продукты с одинаковым размером
        const sellerProductsMap = new Map()
        filteredItems.forEach((item) => {
          const sizeKey = `${item.size}-${
            item.size_type?.toUpperCase() || "OTHER"
          }`
          if (!sellerProductsMap.has(sizeKey)) {
            sellerProductsMap.set(sizeKey, [])
          }
          sellerProductsMap.get(sizeKey).push({
            id: item.id,
            price: item.price ? Number(item.price) : 0,
            isTrade: item.is_trade || false,
            sellerId: item.seller?.id,
            sellerName: item.seller?.username || "Продавец",
            sellerRating: item.seller?.rating ? Number(item.seller.rating) : 0,
            count:
              item.count !== undefined && item.count !== null
                ? Number(item.count)
                : null,
          })
        })

        // Определяем, нужен ли запрос getProduct
        // Используем существующий продукт из заявки или первый продукт продавца
        let productId = null
        if (existingItem && existingItem.id) {
          productId = existingItem.id
        } else if (filteredItems.length > 0 && filteredItems[0].id) {
          productId = filteredItems[0].id
        }

        // Если нет продукта, используем только данные из модели
        if (!productId) {
          this.sizes = filteredItems.map((item) => ({
            id: item.id,
            size: item.size,
            sizeType: item.size_type,
            price: item.price ? Number(item.price) : 0,
            isAvailable: item.is_available !== false,
            isTrade: item.is_trade || false,
            sellerId: item.seller?.id,
            sellerName: item.seller?.username || "Продавец",
            sellerRating: item.seller?.rating ? Number(item.seller.rating) : 0,
            productId: item.id,
            count:
              item.count !== undefined && item.count !== null
                ? Number(item.count)
                : null,
          }))
        } else {
          // Запускаем запрос getProduct параллельно с обработкой данных
          // Формируем размеры из данных модели, пока грузится getProduct
          const productPromise = this.$store.dispatch("getProduct", {
            id: productId,
          })

          // Сначала формируем размеры из доступных данных продавца для быстрого отображения
          const tempSizesFromSeller = new Map()
          filteredItems.forEach((item) => {
            const sizeKey = `${item.size}-${
              item.size_type?.toUpperCase() || "OTHER"
            }`
            if (!tempSizesFromSeller.has(sizeKey)) {
              tempSizesFromSeller.set(sizeKey, {
                id: item.id, // Используем id продукта как временный id
                size: item.size,
                sizeType: item.size_type,
                price: item.price ? Number(item.price) : 0,
                isAvailable: item.is_available !== false,
                isTrade: item.is_trade || false,
                sellerId: item.seller?.id,
                sellerName: item.seller?.username || "Продавец",
                sellerRating: item.seller?.rating
                  ? Number(item.seller.rating)
                  : 0,
                productId: item.id,
                count:
                  item.count !== undefined && item.count !== null
                    ? Number(item.count)
                    : null,
              })
            }
          })

          // Показываем доступные размеры сразу
          if (tempSizesFromSeller.size > 0) {
            this.sizes = Array.from(tempSizesFromSeller.values())
            this.isLoading = false // Разблокируем UI
            await this.$nextTick() // Даем UI обновиться
          }

          // Получаем полные данные размеров
          try {
            const productResponse = await productPromise
            const productData = productResponse?.data || productResponse
            const apiSizes = productData.sizes?.items || []

            if (apiSizes.length > 0) {
              // Формируем список размеров из sizes.items
              const tempSizes = []
              const usedProductIds = new Set()

              apiSizes.forEach((sizeItem) => {
                const sizeKey = `${sizeItem.size}-${
                  sizeItem.size_type?.toUpperCase() || "OTHER"
                }`
                const sellerProducts = sellerProductsMap.get(sizeKey)

                let productData = {
                  id: sizeItem.id,
                  size: sizeItem.size,
                  sizeType: sizeItem.size_type,
                  isAvailable: sizeItem.is_available === true,
                }

                if (sellerProducts && sellerProducts.length > 0) {
                  const sellerProduct =
                    sellerProducts.find((p) => !usedProductIds.has(p.id)) ||
                    sellerProducts[0]

                  if (sellerProduct) {
                    usedProductIds.add(sellerProduct.id)
                    productData = {
                      ...productData,
                      productId: sellerProduct.id,
                      price: sellerProduct.price,
                      isTrade: sellerProduct.isTrade,
                      sellerId: sellerProduct.sellerId,
                      sellerName: sellerProduct.sellerName,
                      sellerRating: sellerProduct.sellerRating,
                      count:
                        sellerProduct.count !== undefined
                          ? Number(sellerProduct.count)
                          : null,
                    }
                  } else {
                    const firstProduct = sellerProducts[0]
                    productData = {
                      ...productData,
                      productId: firstProduct.id,
                      price: firstProduct.price,
                      isTrade: firstProduct.isTrade,
                      sellerId: firstProduct.sellerId,
                      sellerName: firstProduct.sellerName,
                      sellerRating: firstProduct.sellerRating,
                      count:
                        firstProduct.count !== undefined
                          ? Number(firstProduct.count)
                          : null,
                    }
                  }
                } else {
                  const firstSellerProduct = filteredItems[0]
                  productData = {
                    ...productData,
                    productId: null,
                    price: 0,
                    isTrade: false,
                    sellerId: firstSellerProduct?.seller?.id,
                    sellerName:
                      firstSellerProduct?.seller?.username || "Продавец",
                    sellerRating: firstSellerProduct?.seller?.rating
                      ? Number(firstSellerProduct.seller.rating)
                      : 0,
                  }
                }

                tempSizes.push(productData)
              })

              this.sizes = tempSizes
            }
          } catch (error) {
            console.error("Ошибка при получении размеров продукта:", error)
            // Оставляем размеры из filteredItems, если запрос getProduct не удался
          }
        }

        // Сортируем размеры по типу и значению
        const groupedSizes = {}
        this.sizes.forEach((size) => {
          const type = (size.sizeType || "OTHER").toUpperCase()
          if (!groupedSizes[type]) {
            groupedSizes[type] = []
          }
          groupedSizes[type].push(size)
        })

        // Сортируем размеры внутри каждого типа
        Object.keys(groupedSizes).forEach((type) => {
          groupedSizes[type].sort((a, b) => {
            const aValue = this.parseSizeValue(a.size)
            const bValue = this.parseSizeValue(b.size)
            return aValue - bValue
          })
        })

        // Объединяем все размеры обратно в один массив в правильном порядке
        const orderedTypes = ["US", "EU", "RU", "UK", "CM", "OTHER"]
        this.sizes = []
        orderedTypes.forEach((type) => {
          if (groupedSizes[type]) {
            this.sizes.push(...groupedSizes[type])
          }
        })
        // Добавляем остальные типы, которых нет в списке
        Object.keys(groupedSizes).forEach((type) => {
          if (!orderedTypes.includes(type)) {
            this.sizes.push(...groupedSizes[type])
          }
        })
      } catch (error) {
        console.error("Ошибка при получении размеров:", error)
        window.alert("Не удалось загрузить размеры")
      } finally {
        this.isLoading = false
      }
    },
    parseSizeValue(size) {
      const parsed = parseFloat(size)
      return isNaN(parsed) ? 0 : parsed
    },
    selectSize(sizeId) {
      // Получаем значение из события и приводим к числу для единообразия
      const id = typeof sizeId === "string" ? Number(sizeId) : sizeId
      this.selectedSizeId = id
      console.log("Selected size ID:", id, "Type:", typeof id)
    },
    setupMainButton() {
      const tg = window.Telegram?.WebApp
      const mb = tg?.MainButton
      if (!mb) return

      this.mainButtonInstance = mb
      this.updateMainButton()
    },
    updateMainButton() {
      const mb = this.mainButtonInstance
      if (!mb) return

      if (this.selectedSizeId) {
        if (this.mbHandler) {
          mb.offClick(this.mbHandler)
        }
        this.mbHandler = this.addToBid

        mb.setText("Добавить")
        mb.color = "#f07818"
        mb.textColor = "#ffffff"
        mb.onClick(this.mbHandler)
        mb.show()
      } else {
        mb.hide()
      }
    },
    destroyMainButton() {
      const mb = this.mainButtonInstance
      if (mb) {
        if (this.mbHandler) {
          mb.offClick(this.mbHandler)
        }
        mb.hide()
      }
      this.mainButtonInstance = null
      this.mbHandler = null
    },
    async addToBid() {
      if (!this.selectedSizeId || !this.selectedSizeItem) {
        window.alert("Выберите размер")
        return
      }

      const sizeItem = this.selectedSizeItem

      // Получаем информацию о модели из текущей заявки
      const bidItems = this.$store.state.bid || []
      const existingItem = bidItems.find(
        (item) => String(item.modelId) === String(this.modelId)
      )

      // Проверяем количество доступных продуктов для этого конкретного размера
      const maxCountForSize =
        sizeItem.count !== undefined && sizeItem.count !== null
          ? Number(sizeItem.count)
          : null

      if (maxCountForSize !== null) {
        // Находим все продукты в заявке с тем же ID продукта (тот же размер и тот же продукт)
        const productId = sizeItem.productId || sizeItem.id
        const existingItemsForSameProduct = bidItems.filter(
          (item) => String(item.id) === String(productId)
        )

        // Подсчитываем общее количество продуктов этого конкретного размера в заявке
        const currentQuantityForSize = existingItemsForSameProduct.reduce(
          (sum, item) => sum + (item.quantity || 1),
          0
        )

        // Проверяем, не превышает ли добавление нового продукта максимальное количество для этого размера
        if (currentQuantityForSize + 1 > maxCountForSize) {
          const tg = window.Telegram?.WebApp
          if (tg?.showPopup) {
            tg.showPopup({
              title: "Превышен лимит",
              message:
                "Вы уже выбрали максимум товаров из того, что в наличии. Доступно: " +
                maxCountForSize +
                " шт.",
              buttons: [{ type: "close", text: "Ок" }],
            })
          } else {
            window.alert(
              "Вы уже выбрали максимум товаров из того, что в наличии. Доступно: " +
                maxCountForSize +
                " шт."
            )
          }
          return
        }
      }

      let modelInfo = {
        modelTitle: this.model?.title || existingItem?.modelTitle || "",
        modelImages: this.model?.images || existingItem?.modelImages || [],
        preview_image:
          this.model?.images?.[0] || existingItem?.preview_image || null,
        modelArticle: existingItem?.modelArticle,
        total_count: existingItem?.total_count || this.totalAvailableCount,
        min_price: existingItem?.min_price,
      }

      const bidItem = {
        // Информация о продукте
        // Используем productId (ID продукта продавца) для добавления в заявку
        // sizeItem.id - это ID размера из sizes.items, используется только для выбора
        id: sizeItem.productId || sizeItem.id,
        modelId: this.modelId,
        price: sizeItem.price || 0,
        size: sizeItem.size,
        sizeType: sizeItem.sizeType,
        isTrade: sizeItem.isTrade || false,
        sellerId: sizeItem.sellerId,
        sellerName: sizeItem.sellerName,
        sellerRating: sizeItem.sellerRating,
        // Информация о модели
        ...modelInfo,
        // Максимальное доступное количество продукта
        maxCount:
          sizeItem.count !== undefined && sizeItem.count !== null
            ? Number(sizeItem.count)
            : null,
        // Количество
        quantity: 1,
        // Пользовательская цена (если isTrade)
        customPrice: null,
      }

      try {
        this.$store.commit("addToBid", bidItem)
        // Возвращаемся назад
        this.$router.go(-1)
      } catch (error) {
        window.alert(error.message || "Не удалось добавить товар в заявку")
      }
    },
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 1rem 1rem 6.5rem 1rem;
  min-height: 100vh;
}

.loading,
.error {
  text-align: center;
  padding: 3rem 1rem;
  color: #7b7b81;
  font-size: 1rem;
}

.section {
  margin-bottom: 2rem;
}

.sectionTitle {
  font-size: 0.875rem;
  color: #7b7b81;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}

.sizesList {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: #f5f7fa;
  border-radius: 0.75rem;
  border: 1px solid #ebeaea;
}

.sizeItem {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.75rem 0.75rem 0 0.75rem;
  padding: 0 0 0.75rem 0;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #00000024;

  &:last-child {
    border-bottom: 1px solid #00000000;
  }

  &:hover:not(.disabled) {
    background: #f5f7fa;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.radioInput {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid #dbdbdf;
  border-radius: 50%;
  background: transparent;
  position: relative;
  margin: 0;
  flex-shrink: 0;

  &:checked {
    border-color: #f07818;
    border-width: 2px;
    background: transparent;

    &::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background: #f07818;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.sizeLabel {
  font-size: 1rem;
  font-weight: 500;
  color: #000;
  flex: 1;
}
</style>
